import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase.js';
import { bedmapsListingIds, listPartners, loadBedmapsListing, saveBedmapsListing, setBedmapsListings, setPartnerStage } from './adminData.js';

// The support needs bedmaps.com's search filters on, so a listing can only use these.
const SUPPORT_WORDS = ['Domestic abuse', 'Refugees and asylum seekers', 'Veterans', 'Mental health', 'Young people', 'Leaving care', 'Women only', 'Drug and alcohol', 'Moving on from homelessness'];
const FIELDS = [
  ['public_name', 'Name on BedMaps', 'text', true],
  ['town', 'Town', 'text', true],
  ['area', 'Area they cover', 'text', true],
  ['website', 'Website', 'url', false],
  ['email', 'Email', 'email', false],
  ['lat', 'Latitude', 'text', false],
  ['lng', 'Longitude', 'text', false],
];

/** An on and off switch. Clicking it asks for the opposite of `on`. */
export function StageSwitch({ label, on, disabled = false, onChange }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} className={`stage-switch${on ? ' is-on' : ''}`} disabled={disabled} onClick={() => onChange(!on)}>
      <span aria-hidden="true"/>
    </button>
  );
}

/** What a partner shows on bedmaps.com. Saving puts them on it, so what they do and how to apply have to be written. */
function ListingForm({ draft, busy, error, onSave, onCancel }) {
  const [form, setForm] = useState(() => ({
    ...Object.fromEntries(FIELDS.map(([key]) => [key, draft[key] ?? ''])),
    summary: draft.summary ?? '',
    referral: draft.referral ?? '',
    support_types: Array.isArray(draft.support_types) ? draft.support_types : [],
  }));
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const flip = (word) => setForm((current) => ({
    ...current,
    support_types: current.support_types.includes(word) ? current.support_types.filter((item) => item !== word) : [...current.support_types, word],
  }));
  const words = [...new Set([...SUPPORT_WORDS, ...form.support_types])];
  const title = `What ${draft.public_name || 'they'} shows on BedMaps`;
  return (
    <form className="admin-partner-form" aria-label={title} onSubmit={(event) => { event.preventDefault(); onSave(form); }}>
      <h3 className="admin-subsection">{title}</h3>
      <p className="mod-note">Anyone can read this on bedmaps.com. A position should only be as exact as the town, and can stay blank if we know the town.</p>
      <div className="admin-partner-grid">
        {FIELDS.map(([key, label, kind, required]) => (
          <label key={key}>{label}
            <input type={kind} value={form[key]} onChange={set(key)} required={required} maxLength={200}/>
          </label>
        ))}
      </div>
      <label className="admin-partner-wide">What they do
        <textarea rows={2} value={form.summary} onChange={set('summary')} required maxLength={1000}/>
      </label>
      <label className="admin-partner-wide">How to apply
        <textarea rows={3} value={form.referral} onChange={set('referral')} required maxLength={1000}/>
      </label>
      <div className="stage-words" role="group" aria-label="Who they support">
        {words.map((word) => (
          <button key={word} type="button" aria-pressed={form.support_types.includes(word)} className={form.support_types.includes(word) ? 'is-on' : ''} onClick={() => flip(word)}>{word}</button>
        ))}
      </div>
      {error && <p className="mod-error" role="alert">{error}</p>}
      <div className="admin-head-actions">
        <button type="submit" className="button button-dark" disabled={busy}>{draft.listed ? 'Save changes' : 'Put on BedMaps'}</button>
        <button type="button" className="button button-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/**
 * A housing provider's two switches: from their reply to partner, and from partner to on BedMaps. Reads who is where, and changes them.
 * `enabled` false reads nothing. `onChanged` runs after the first switch moves, for a list that only shows partners.
 */
export function useProviderStages({ enabled = true, onChanged } = {}) {
  const [state, setState] = useState({ partners: new Map(), listed: new Set(), ready: false });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');

  const read = useCallback(async () => {
    const [partners, ids] = await Promise.all([listPartners(supabase), bedmapsListingIds(supabase)]);
    setState({
      partners: new Map((Array.isArray(partners?.rows) ? partners.rows : []).map((partner) => [partner.provider_id, partner])),
      listed: new Set(Array.isArray(ids?.partners) ? ids.partners : []),
      ready: true,
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    read().catch((cause) => setError(cause.message || 'Could not read who is a partner. Please try again.'));
  }, [enabled, read]);

  const attempt = async (work) => {
    setBusy(true);
    setError('');
    try {
      await work();
    } catch (cause) {
      setError(cause.message || 'Could not change that. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const setPartner = (id, on, reply) => attempt(async () => {
    await setPartnerStage(supabase, id, on, reply);
    if (!on && draft?.provider_id === id) setDraft(null);
    await read();
    onChanged?.();
  });

  // Off takes them off and keeps the wording. On goes straight back if it is written, and asks for it if not.
  const setListing = (id, on) => attempt(async () => {
    if (on) {
      const listing = await loadBedmapsListing(supabase, id);
      if (!listing?.ready) {
        setFormError('');
        setDraft(listing);
        return;
      }
    }
    await setBedmapsListings(supabase, 'partner', [id], on);
    await read();
  });

  const editWording = (id) => attempt(async () => {
    setFormError('');
    setDraft(await loadBedmapsListing(supabase, id));
  });

  const saveWording = async (form) => {
    setBusy(true);
    setFormError('');
    try {
      await saveBedmapsListing(supabase, draft.provider_id, form);
      setDraft(null);
      await read();
    } catch (cause) {
      setFormError(cause.message || 'Could not save that. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return { ...state, busy, error, draft, formError, setPartner, setListing, editWording, saveWording, closeWording: () => setDraft(null) };
}

/** The wording form, when one is open, and anything that went wrong with a switch. */
export function StagePanel({ stages }) {
  return (
    <>
      {stages.error && <p className="mod-error" role="alert">{stages.error}</p>}
      {stages.draft && (
        <ListingForm key={stages.draft.provider_id} draft={stages.draft} busy={stages.busy} error={stages.formError} onSave={(form) => void stages.saveWording(form)} onCancel={stages.closeWording}/>
      )}
    </>
  );
}

/** The first switch. Someone who said no can't be made a partner until what they said is changed. */
export function PartnerSwitch({ stages, id, name, reply }) {
  const partner = stages.partners.get(id);
  return (
    <StageSwitch label={`${name} is a partner`} on={Boolean(partner?.is_partner)} disabled={stages.busy || partner?.status === 'declined'}
      onChange={(on) => void stages.setPartner(id, on, reply)}/>
  );
}

/** The second switch, which only works for a partner, with a way to change the wording once they are on. */
export function ListingSwitch({ stages, id, name }) {
  const on = stages.listed.has(id);
  return (
    <span className="stage-cell">
      <StageSwitch label={`Show ${name} on BedMaps`} on={on} disabled={stages.busy || !stages.partners.get(id)?.is_partner} onChange={(next) => void stages.setListing(id, next)}/>
      {on && <button type="button" className="admin-link" disabled={stages.busy} aria-label={`Change ${name}'s BedMaps wording`} onClick={() => void stages.editWording(id)}>Wording</button>}
    </span>
  );
}
